import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppState } from "../../../../features/AppState";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import {
  FETCHER_CAPABILITY,
  FETCHER_VIEW_PERMISSION,
} from "../helpers/fetcherPermissions";
import {
  parseDuplicateOf,
  rowId,
  selectCatalogueRows,
} from "../helpers/fetcherCatalogue";
import { SortDirection } from "../helpers/tableSorting";
import {
  readPageParam,
  readRowsPerPageParam,
  readSearchParam,
  writePageParam,
  writeRowsPerPageParam,
  writeSearchParam,
} from "../helpers/urlParams";
import DeleteCatalogueItemModal from "../DeleteCatalogueItemModal";
import ReportBrandsModal from "./ReportBrandsModal";
import ReportsFilterCard from "./ReportsFilterCard";
import ReportsSkeleton from "./ReportsSkeleton";
import ReportsTable, { ReportTableRow } from "./ReportsTable";
import CreateReportModal, { ReportDraftSeed } from "./CreateReportModal";
import CreateInterfaceModal from "../Interfaces/CreateInterfaceModal";
import EditReportModal from "./EditReportModal";
import ViewReportModal from "./ViewReportModal";

const DEFAULT_SORT_KEY = "id";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

const Reports = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userData = useSelector(selectUserData);
  const canEdit = CheckPermissions(
    userData?.data?.products?.data_collection,
    FETCHER_CAPABILITY,
    FETCHER_VIEW_PERMISSION,
  );

  const { fetcherSettingsMainTablesData, isCatalogueLoading } = useSelector(
    (state: AppState) => state.fetcherSettings,
  );

  // the catalogue query returns every level in one list; this tab wants the
  // report rows
  const reportRows = useMemo(
    () => selectCatalogueRows(fetcherSettingsMainTablesData, "report"),
    [fetcherSettingsMainTablesData],
  );
  const selectedName = readSearchParam(searchParams.get("search"));
  const page = readPageParam(searchParams.get("page"));
  const rowsPerPage = readRowsPerPageParam(searchParams.get("page_size"));
  const [selectedInterfaces, setSelectedInterfaces] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState(DEFAULT_SORT_KEY);
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    DEFAULT_SORT_DIRECTION,
  );
  const [brandsModalId, setBrandsModalId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [createSeed, setCreateSeed] = useState<ReportDraftSeed | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreateInterfaceOpen, setIsCreateInterfaceOpen] = useState(false);
  const [editing, setEditing] = useState<{
    id: string;
    mode: "view" | "edit";
  } | null>(null);

  const searchOptions = useMemo(
    () =>
      [
        ...new Set(
          reportRows
            .map((row) => row.item_name)
            .filter((name): name is string => !!name),
        ),
      ].sort((a, b) => a.localeCompare(b)),
    [reportRows],
  );

  const interfaceOptions = useMemo(() => {
    const byId = new Map<string, string>();
    reportRows.forEach((row) => {
      const id = rowId(row.interface_id);
      if (id && !byId.has(id)) {
        byId.set(id, `${row.interface_name ?? "?"} (${id})`);
      }
    });
    return [...byId.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [reportRows]);

  const rows: ReportTableRow[] = useMemo(
    () =>
      reportRows
        .filter(
          (row) =>
            (!selectedName || row.item_name === selectedName) &&
            (selectedInterfaces.length === 0 ||
              selectedInterfaces.includes(rowId(row.interface_id))),
        )
        .map((row) => ({
          id: rowId(row.item_id),
          name: row.item_name ?? "",
          interfaceName: row.interface_name ?? "-",
          collectionType: row.collection_type ?? "",
          urlPath: row.url_path || row.uri_value || "",
          configs: row.child_configs ?? 0,
          objects: row.own_objects ?? 0,
          brands: row.used_by_brands ?? 0,
          // advisory only - matched server-side on interface + collection type
          // and the name with an abandoned-copy suffix removed
          twinIds: parseDuplicateOf(row.duplicate_of),
        })),
    [reportRows, selectedName, selectedInterfaces],
  );

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
    writePageParam(setSearchParams, 0);
  };

  const openDuplicate = (id: string) => {
    const report = reportRows.find((row) => rowId(row.item_id) === id);
    if (!report) return;
    setEditing(null);
    setCreateSeed({
      name: `${report.item_name ?? ""}_copy`,
      iface: rowId(report.interface_id),
      ctype_id: rowId(report.collection_type_id),
      uri: report.uri_value ?? "",
      // the report's own objects are not in the catalogue response, so a
      // duplicate starts with none until that read exists
      objects: [],
    });
    setIsCreateOpen(true);
  };

  return (
    <>
      <ReportsFilterCard
        searchOptions={searchOptions}
        selectedName={selectedName}
        onSelectName={(value) => writeSearchParam(setSearchParams, value)}
        interfaceOptions={interfaceOptions}
        selectedInterfaces={selectedInterfaces}
        onSelectedInterfacesChange={setSelectedInterfaces}
        onClearFilters={() => setSelectedInterfaces([])}
        canCreate={canEdit}
        onCreate={() => {
          setCreateSeed(null);
          setIsCreateOpen(true);
        }}
      />

      {isCatalogueLoading ? (
        <ReportsSkeleton />
      ) : (
        <ReportsTable
          rows={rows}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(value) => writePageParam(setSearchParams, value)}
          onRowsPerPageChange={(value) =>
            writeRowsPerPageParam(setSearchParams, value)
          }
          canEdit={canEdit}
          onView={(id) => setEditing({ id, mode: "view" })}
          onEdit={(id) => setEditing({ id, mode: canEdit ? "edit" : "view" })}
          onDuplicate={openDuplicate}
          onDelete={setDeleteId}
          onShowBrands={setBrandsModalId}
          onOpenTwin={(id) => setEditing({ id, mode: "view" })}
        />
      )}

      <CreateReportModal
        open={isCreateOpen}
        initial={createSeed}
        onClose={() => setIsCreateOpen(false)}
      />

      <CreateInterfaceModal
        open={isCreateInterfaceOpen}
        onClose={() => setIsCreateInterfaceOpen(false)}
      />

      {editing?.mode === "view" && (
        <ViewReportModal
          open
          reportId={editing.id}
          onClose={() => setEditing(null)}
          onSwitchToEdit={() =>
            setEditing((previous) =>
              previous ? { ...previous, mode: "edit" } : previous,
            )
          }
          onDuplicate={openDuplicate}
          onShowBrands={setBrandsModalId}
          onOpenTwin={(id) => setEditing({ id, mode: "view" })}
          onCreateInterface={() => setIsCreateInterfaceOpen(true)}
          canEdit={canEdit}
        />
      )}

      {editing?.mode === "edit" && (
        <EditReportModal
          open
          reportId={editing.id}
          mode="edit"
          onClose={() => setEditing(null)}
          onSwitchToEdit={() =>
            setEditing((previous) =>
              previous ? { ...previous, mode: "edit" } : previous,
            )
          }
          onDuplicate={openDuplicate}
          onShowBrands={setBrandsModalId}
        />
      )}

      {brandsModalId && (
        <ReportBrandsModal
          open
          reportId={brandsModalId}
          onClose={() => setBrandsModalId(null)}
        />
      )}

      {deleteId && (
        <DeleteCatalogueItemModal
          open
          level="report"
          itemId={deleteId}
          onClose={() => setDeleteId(null)}
        />
      )}
    </>
  );
};

export default Reports;
